import {
  DIGISAKA_API_BLS_FORECAST,
  digisakaAuthRequestInit,
  gptInferenceJsonHeaders,
} from '../../../config/digisakaGptInference';
import { notifyInferenceComputeUsage } from '../../../utils/inferenceComputeBudget';

export type BlsDataset = 'plantation_practice' | 'all_leads_products' | 'without_protectant';
export type BlsTarget = 'YLI' | 'YLS' | 'TFL' | 'Shooting';

export const BLS_PRACTICES: {
  value: BlsDataset;
  label: string;
  shortLabel: string;
  emoji: string;
  hint: string;
  about: string;
}[] = [
  {
    value: 'plantation_practice',
    label: 'Usual plantation practice',
    shortLabel: 'Usual practice',
    emoji: '🌱',
    hint: 'Normal farm care — usual sprays, deleafing, and field work.',
    about:
      'Choose this if this field is run the usual way: regular spray rounds, cutting out badly diseased leaves, and normal field work. Not a special full-product program, and protectant is still used.',
  },
  {
    value: 'all_leads_products',
    label: 'Full spray / lead-product program',
    shortLabel: 'Full spray program',
    emoji: '🧴',
    hint: 'Using the full set of protectant and systemic products for black sigatoka.',
    about:
      'Choose this if this field follows a strong black-sigatoka program: protectant sprays plus the “lead” systemic products on a tight calendar. Leaf numbers are often a bit healthier than usual practice.',
  },
  {
    value: 'without_protectant',
    label: 'No protectant spray',
    shortLabel: 'No protectant',
    emoji: '🚫',
    hint: 'Not applying a protectant (protectant skipped or not used).',
    about:
      'Choose this if this field is not getting a protectant (the contact spray that coats the leaf) — skipped, ran out, or left off. Disease often moves onto younger leaves faster.',
  },
];

export const BLS_TARGETS: {
  value: BlsTarget;
  label: string;
  shortLabel: string;
  subtitle: string;
  about: string;
}[] = [
  {
    value: 'YLI',
    label: 'Infection on young leaves',
    shortLabel: 'Infection',
    subtitle: 'Reddish-brown streaks under the leaf',
    about:
      'Infection: reddish-brown to dark brown streaks on the underside of the leaf that gradually widen and expand. The number is the youngest leaf (from the top) that already shows these streaks. Leaf 1 is the newest leaf. A lower number is worse — streaks have reached younger leaves.',
  },
  {
    value: 'YLS',
    label: 'Spots on young leaves',
    shortLabel: 'Spots',
    subtitle: 'Whitish to rusty brown specks under the leaf',
    about:
      'Spots: whitish or yellowish specks on the underside of the leaf that then turn into long rusty brown specks. The number is the youngest leaf (from the top) that already has these spots. This is usually an older leaf than the streaked (infection) leaf. A lower number is worse.',
  },
  {
    value: 'TFL',
    label: 'Working (green) leaves',
    shortLabel: 'Working leaves',
    subtitle: 'How much healthy green leaf is left',
    about:
      'How many leaves are still more than half green — the working canopy. A higher number is better. Export fruit needs enough working leaves until harvest.',
  },
  {
    value: 'Shooting',
    label: 'Leaves at flowering',
    shortLabel: 'Flowering',
    subtitle: 'Leaf health near the fruit bunch',
    about:
      'Working leaves when the bunch comes out (shooting). A higher number is better. Fewer leaves here means a weaker bunch.',
  },
];

export type BlsRiskLevel = 'low' | 'watch' | 'high' | 'severe';

export type BlsRiskStatus = {
  level: BlsRiskLevel;
  label: string;
  hint: string;
};

const RISK_RANK: Record<BlsRiskLevel, number> = { low: 0, watch: 1, high: 2, severe: 3 };

/** Farmer status from next-week leaf number (export-banana rules of thumb, not a lab grade). */
export function blsRiskStatus(target: BlsTarget, value: number): BlsRiskStatus {
  if (target === 'YLI') {
    if (value >= 6)
      return {
        level: 'low',
        label: 'Looking okay',
        hint: `Looking okay for now — infection streaks still look mostly on older leaves. Confirm when you scout.`,
      };
    if (value >= 5)
      return {
        level: 'watch',
        label: 'Needs attention',
        hint: `Needs attention — infection streaks are starting to move toward younger leaves. Check spray and leaf cutting when you walk the field.`,
      };
    if (value >= 4)
      return {
        level: 'high',
        label: 'Needs action soon',
        hint: `Needs action soon — infection streaks (reddish-brown to dark brown under the leaf) are reaching young leaves. Walk the field to confirm.`,
      };
    return {
      level: 'severe',
      label: 'Check the field now',
      hint: `Check the field now — infection on young leaves looks bad. Confirm in the field.`,
    };
  }
  if (target === 'YLS') {
    if (value >= 8)
      return {
        level: 'low',
        label: 'Looking okay',
        hint: `Looking okay for now — spots still look mostly on older leaves. Confirm when you scout.`,
      };
    if (value >= 6.5)
      return {
        level: 'watch',
        label: 'Needs attention',
        hint: `Needs attention — whitish/yellowish to rusty brown spots are starting to climb. Keep canopy care on time.`,
      };
    if (value >= 5.5)
      return {
        level: 'high',
        label: 'Needs action soon',
        hint: `Needs action soon — rusty brown spots are reaching young leaves. Confirm in the field.`,
      };
    return {
      level: 'severe',
      label: 'Check the field now',
      hint: `Check the field now — spotting on young leaves looks bad. Walk the field to confirm.`,
    };
  }
  if (target === 'TFL') {
    if (value >= 10)
      return {
        level: 'low',
        label: 'Looking okay',
        hint: `Looking okay — plenty of green working leaves for the plant.`,
      };
    if (value >= 8)
      return {
        level: 'watch',
        label: 'Needs attention',
        hint: `Needs attention — green working leaves are only fair. Protect what is left.`,
      };
    if (value >= 7)
      return {
        level: 'high',
        label: 'Needs action soon',
        hint: `Needs action soon — not enough green working leaves for good fruit filling.`,
      };
    return {
      level: 'severe',
      label: 'Check the field now',
      hint: `Check the field now — very few green working leaves left. Confirm in the field.`,
    };
  }
  if (value >= 10)
    return {
      level: 'low',
      label: 'Looking okay',
      hint: `Looking okay — leaves around the bunch look healthy.`,
    };
  if (value >= 8)
    return {
      level: 'watch',
      label: 'Needs attention',
      hint: `Needs attention — leaves around the bunch are only fair.`,
    };
  if (value >= 7)
    return {
      level: 'high',
      label: 'Needs action soon',
      hint: `Needs action soon — leaves around the bunch look weak.`,
    };
  return {
    level: 'severe',
    label: 'Check the field now',
    hint: `Check the field now — leaves around the bunch look very weak. Confirm in the field.`,
  };
}

/**
 * Overall field status from all four leaf scores.
 * Uses the *average* risk level (not the single worst), so one High infection
 * score does not always force the whole banner to High when canopy/spots look fine.
 */
export function worstBlsRisk(statuses: BlsRiskStatus[]): BlsRiskStatus {
  if (!statuses.length) {
    return {
      level: 'low',
      label: 'Low pressure',
      hint: 'No big change expected next week.',
    };
  }

  const avg =
    statuses.reduce((sum, s) => sum + RISK_RANK[s.level], 0) / statuses.length;
  let level: BlsRiskLevel;
  if (avg < 0.75) level = 'low';
  else if (avg < 1.5) level = 'watch';
  else if (avg < 2.35) level = 'high';
  else level = 'severe';

  const labels: Record<BlsRiskLevel, string> = {
    low: 'Looking okay',
    watch: 'Needs attention',
    high: 'Needs action soon',
    severe: 'Check the field now',
  };

  return {
    level,
    label: labels[level],
    hint: '',
  };
}

/** Plain farmer alert from all four statuses — no leaf numbers. */
export function blsOverallFarmerAlert(
  items: { target: BlsTarget; level: BlsRiskLevel }[],
  overallLevel: BlsRiskLevel,
  forecastDay?: string
): string {
  const byTarget = (t: BlsTarget) => items.find((x) => x.target === t)?.level;

  const infection = (level?: BlsRiskLevel) => {
    if (level === 'severe')
      return 'Infection: dark reddish-brown streaks under the leaf look bad on young leaves.';
    if (level === 'high')
      return 'Infection: dark reddish-brown streaks under the leaf are reaching young leaves.';
    if (level === 'watch')
      return 'Infection: streaks are starting to move toward younger leaves.';
    if (level === 'low')
      return 'Infection: streaks still look mostly on older leaves.';
    return null;
  };

  const spots = (level?: BlsRiskLevel) => {
    if (level === 'severe')
      return 'Spots: whitish then rusty brown specks under the leaf look bad on young leaves.';
    if (level === 'high')
      return 'Spots: those rusty brown specks under the leaf are reaching young leaves.';
    if (level === 'watch')
      return 'Spots: early whitish/yellowish to rusty brown specks are starting to climb.';
    if (level === 'low')
      return 'Spots: early whitish/yellowish to rusty brown specks still look on older leaves.';
    return null;
  };

  const working = (level?: BlsRiskLevel) => {
    if (level === 'severe') return 'Working leaves: very little green leaf left for the plant to work with.';
    if (level === 'high') return 'Working leaves: not enough green leaf left for good fruit filling.';
    if (level === 'watch') return 'Working leaves: green leaf cover is only fair.';
    if (level === 'low') return 'Working leaves: plenty of healthy green leaf.';
    return null;
  };

  const flowering = (level?: BlsRiskLevel) => {
    if (level === 'severe') return 'Flowering: leaves around the bunch look very weak.';
    if (level === 'high') return 'Flowering: leaves around the bunch look weak.';
    if (level === 'watch') return 'Flowering: leaves around the bunch are only fair.';
    if (level === 'low') return 'Flowering: leaves around the bunch look healthy.';
    return null;
  };

  const lines = [
    infection(byTarget('YLI')),
    spots(byTarget('YLS')),
    working(byTarget('TFL')),
    flowering(byTarget('Shooting')),
  ].filter(Boolean) as string[];

  const overallPhrase: Record<BlsRiskLevel, string> = {
    low: 'Overall the field looks okay for now.',
    watch: 'Overall: some parts need attention.',
    high: 'Overall: act soon on the weaker parts above.',
    severe: 'Overall: check this field as soon as you can.',
  };

  const when = forecastDay ? ` For ${forecastDay}.` : '';
  return `${lines.join(' ')} ${overallPhrase[overallLevel]}${when} This is a model outlook, not a photo of these plants — confirm when you scout.`;
}

export function blsTrendArrow(target: BlsTarget, now: number, next: number): '↑' | '↓' | '→' {
  const d = next - now;
  if (Math.abs(d) < 0.05) return '→';
  // YLI/YLS: higher leaf rank is better; TFL/Shooting: higher count is better — both use ↑ when next > now
  return d > 0 ? '↑' : '↓';
}

/** 0–100 bar width for the leaf gauge (demo uses value/10). */
export function blsGaugePct(value: number): number {
  return Math.max(4, Math.min(100, Math.round((value / 12) * 100)));
}

export type BlsForecastPoint = {
  week_start: string;
  week_index: number;
  yhat: number;
  yhat_mean: number;
  yhat_lo: number;
  yhat_hi: number;
  delta: number;
};

export type BlsWeather = {
  avg_temp?: number;
  avg_rh?: number;
  total_rain?: number;
};

export type BlsForecastResult = {
  success: boolean;
  model: string;
  dataset: string;
  practice?: { id: string; label: string; hint: string };
  target: string;
  horizon_weeks: number;
  n_mc: number;
  last_observed: { week_start: string; value: number };
  trend: 'increasing' | 'decreasing' | 'steady' | string;
  weather?: BlsWeather & { source?: string };
  farmer?: {
    metric_label: string;
    metric_short: string;
    metric_hint: string;
    value_how?: string;
    value_meaning?: string;
    weather_meaning?: string;
    outlook: string;
    summary: string;
    advice: string;
  };
  forecast: BlsForecastPoint[];
  history: { week_start: string; value: number }[];
};

function ringCentroid(ring: unknown): { lat: number; lon: number } | null {
  if (!Array.isArray(ring) || ring.length === 0) return null;
  let lat = 0;
  let lon = 0;
  let n = 0;
  for (const pt of ring) {
    if (!Array.isArray(pt) || pt.length < 2) continue;
    const x = Number(pt[0]);
    const y = Number(pt[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    lon += x;
    lat += y;
    n += 1;
  }
  if (n === 0) return null;
  return { lat: lat / n, lon: lon / n };
}

export function centroidFromFieldGeoJson(
  geo: Record<string, unknown> | null | undefined
): { lat: number; lon: number } | null {
  if (!geo) return null;
  const geom =
    geo.type === 'Feature'
      ? ((geo.geometry as Record<string, unknown> | undefined) ?? null)
      : geo.type === 'FeatureCollection'
        ? ((((geo.features as unknown[]) || [])[0] as Record<string, unknown> | undefined)?.geometry as
            | Record<string, unknown>
            | undefined) ?? null
        : geo;
  if (!geom || typeof geom !== 'object') return null;
  const type = geom.type;
  const coords = geom.coordinates;
  if (type === 'Polygon' && Array.isArray(coords)) {
    return ringCentroid(coords[0]);
  }
  if (type === 'MultiPolygon' && Array.isArray(coords) && Array.isArray(coords[0])) {
    return ringCentroid(coords[0][0]);
  }
  if (type === 'Point' && Array.isArray(coords) && coords.length >= 2) {
    const lon = Number(coords[0]);
    const lat = Number(coords[1]);
    if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon };
  }
  return null;
}

export async function fetchBlsForecast(body: {
  dataset?: BlsDataset;
  target?: BlsTarget;
  horizon_weeks?: number;
  n_mc?: number;
  lat?: number;
  lon?: number;
  farm_id?: string;
  weather?: BlsWeather;
}): Promise<BlsForecastResult> {
  const res = await fetch(
    DIGISAKA_API_BLS_FORECAST,
    digisakaAuthRequestInit({
      method: 'POST',
      headers: gptInferenceJsonHeaders(),
      body: JSON.stringify({
        dataset: body.dataset ?? 'plantation_practice',
        target: body.target ?? 'YLI',
        horizon_weeks: body.horizon_weeks ?? 1,
        n_mc: body.n_mc ?? 64,
        ...(body.lat != null && body.lon != null ? { lat: body.lat, lon: body.lon } : {}),
        ...(body.farm_id ? { farm_id: body.farm_id } : {}),
        ...(body.weather ? { weather: body.weather } : {}),
      }),
    })
  );
  notifyInferenceComputeUsage(res);
  if (!res.ok) {
    let detail = `BLS forecast failed (${res.status})`;
    try {
      const err = await res.json();
      if (err?.detail) detail = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  return (await res.json()) as BlsForecastResult;
}
